// Command Center — the flagship view. Combines the Hub Dashboard's visual polish,
// the Analytics anomaly engine, and the Planner's network depth into one page.
// A hub selector flips it between a network overview (All hubs) and a hub deep-dive.
// Shares the transit-aware + sortation (own + received-in) model used everywhere.

import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Card, Field, PageHeader, Select, Spinner } from "../../components/ui";
import { BarsChart, DailyBarsLine, Donut, HBarList, HourBars } from "../../components/charts";
import { getErrorMessage } from "../../lib/errors";
import { addDays, fmtDate, fmtDow, fmtN, startOfWeekSat, today } from "../../lib/format";
import type { Branch, Customer, CustomerLane, Hub, HubEvent } from "../../lib/types";
import { forecastCustomerHub, forecastHubDay, meanAbsPctError } from "../../lib/forecast/engine";
import { hourlyAvailability, lhLoadsInOnDay } from "../../lib/forecast/arrivals";
import { addPackageSplit, emptyPackageSplit, splitPackageMix, type PackageSplit } from "../../lib/forecast/hubMetrics";
import { makeTransitFn, receivedInByDay, transitDaysFor } from "../../lib/forecast/transit";
import { flowShares, shareOf } from "../../lib/forecast/flowShares";
import { useTransitAware } from "../../lib/forecast/useTransitAware";
import { listCustomers } from "../customers/customersApi";
import { listAccountLanes } from "../customers/lanesApi";
import { listHubs } from "../hubs/hubsApi";
import { listBranches } from "../branches/branchesApi";
import { listAccountLhSchedule, type LhLaneRow } from "../lhschedule/lhScheduleApi";
import { listHubEvents } from "../hubevents/hubEventsApi";
import { buildOverrideMap, listAccountOverrides, type ForecastOverride } from "../forecast/overridesApi";
import { listAccountCustomerActuals, type CustomerActual } from "../actuals/customerActualsApi";
import { getFlowShareTotals, type BranchFlow } from "../actuals/branchFlowApi";

const ALL = "__all__";
const PAST_WEEKS = 8;
const FUTURE_WEEKS = 4;
const ACC_WEEKS = 4;
const TYPE_COLORS = { cartons: "#0f766e", polybags: "#0ea5e9", irregular: "#f59e0b", unspec: "#cbd5e1" } as const;
const HUB_PALETTE = ["#0f766e", "#0ea5e9", "#a855f7", "#f59e0b", "#ec4899", "#10b981", "#6366f1", "#ef4444", "#14b8a6", "#f97316", "#8b5cf6", "#84cc16"];
const wkDates = (start: string) => Array.from({ length: 7 }, (_, d) => addDays(start, d));

type Sev = "bad" | "warn" | "info";
interface Anomaly { sev: Sev; cat: string; title: string; detail: string }
const SEV_RANK: Record<Sev, number> = { bad: 0, warn: 1, info: 2 };
const SEV_STYLE: Record<Sev, string> = {
  bad: "border-red-200 bg-red-50 text-red-700",
  warn: "border-amber-200 bg-amber-50 text-amber-700",
  info: "border-sky-200 bg-sky-50 text-sky-700",
};
const SEV_DOT: Record<Sev, string> = { bad: "🔴", warn: "🟠", info: "🔵" };

export default function CommandCenterPage() {
  const { activeAccount } = useAuth();
  const accountId = activeAccount?.accountId ?? null;

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [lanes, setLanes] = useState<Array<{ customerId: string; lane: CustomerLane }>>([]);
  const [overrides, setOverrides] = useState<ForecastOverride[]>([]);
  const [events, setEvents] = useState<HubEvent[]>([]);
  const [actuals, setActuals] = useState<CustomerActual[]>([]);
  const [flow, setFlow] = useState<BranchFlow[]>([]);
  const [lhRows, setLhRows] = useState<LhLaneRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [hubId, setHubId] = useState(ALL);
  const [transitAware, setTransitAware] = useTransitAware();
  const nowWeek = useMemo(() => startOfWeekSat(today()), []);

  useEffect(() => {
    if (!accountId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      listCustomers(accountId),
      listHubs(accountId),
      listBranches(accountId),
      listAccountLanes(accountId),
      listAccountOverrides(accountId),
      listHubEvents(accountId),
      listAccountCustomerActuals(accountId, addDays(nowWeek, -(PAST_WEEKS + 3) * 7), addDays(nowWeek, -1)),
      getFlowShareTotals(accountId, addDays(nowWeek, -28), addDays(nowWeek, -1)),
      listAccountLhSchedule(accountId),
    ])
      .then(([c, h, b, ln, ov, ev, ac, fl, lh]) => {
        setCustomers(c);
        setHubs(h.filter((x) => x.active));
        setBranches(b.filter((x) => x.active));
        setLanes(ln);
        setOverrides(ov);
        setEvents(ev);
        setActuals(ac);
        setFlow(fl);
        setLhRows(lh);
      })
      .catch((e) => setError(getErrorMessage(e, "Failed to load command center.")))
      .finally(() => setLoading(false));
  }, [accountId, nowWeek]);

  const isAll = hubId === ALL;
  const hubSet = useMemo(() => (isAll ? new Set(hubs.map((h) => h.id)) : new Set([hubId])), [isAll, hubs, hubId]);
  const overrideMap = useMemo(() => buildOverrideMap(overrides), [overrides]);
  const shares = useMemo(() => flowShares(flow), [flow]);
  const hubName = useMemo(() => {
    const m = new Map(hubs.map((h) => [h.id, h.code || h.name]));
    return (id: string) => m.get(id) ?? "?";
  }, [hubs]);
  const laneMix = useMemo(() => {
    const m = new Map<string, { cartons: number; polybags: number; irregular: number }>();
    for (const { customerId, lane } of lanes) m.set(`${customerId}|${lane.hubId}`, lane.packageMix);
    return m;
  }, [lanes]);
  const capByKey = useMemo(() => {
    const m = new Map<string, number | null | undefined>();
    for (const { customerId, lane } of lanes) m.set(`${customerId}|${lane.hubId}`, lane.loadCapacity);
    return m;
  }, [lanes]);

  // Windows
  const futureWeeks = useMemo(() => Array.from({ length: FUTURE_WEEKS }, (_, k) => addDays(nowWeek, k * 7)), [nowWeek]);
  const futureDates = useMemo(() => futureWeeks.flatMap(wkDates), [futureWeeks]);
  const maxActualDate = useMemo(() => actuals.reduce((m, a) => (a.date > m ? a.date : m), ""), [actuals]);
  const lastCompleteWeek = useMemo(() => {
    if (!maxActualDate) return addDays(nowWeek, -7);
    let ws = startOfWeekSat(maxActualDate);
    if (addDays(ws, 6) > maxActualDate) ws = addDays(ws, -7);
    return ws;
  }, [maxActualDate, nowWeek]);
  const pastWeeks = useMemo(() => Array.from({ length: PAST_WEEKS }, (_, k) => addDays(lastCompleteWeek, (k - (PAST_WEEKS - 1)) * 7)), [lastCompleteWeek]);
  const accWindow = useMemo(() => {
    const d: string[] = [];
    for (let k = ACC_WEEKS - 1; k >= 0; k--) d.push(...wkDates(addDays(lastCompleteWeek, -k * 7)));
    return d;
  }, [lastCompleteWeek]);
  const curWeekDates = useMemo(() => wkDates(nowWeek), [nowWeek]);

  // Caches
  const ownDay = useMemo(() => {
    const cache = new Map<string, number>();
    return (h: string, d: string) => {
      const k = `${h}|${d}`;
      let v = cache.get(k);
      if (v === undefined) {
        v = forecastHubDay(customers, h, d, overrideMap, events);
        cache.set(k, v);
      }
      return v;
    };
  }, [customers, overrideMap, events]);
  const cFcst = useMemo(() => {
    const cache = new Map<string, number>();
    return (c: Customer, h: string, d: string) => {
      const k = `${c.id}|${h}|${d}`;
      let v = cache.get(k);
      if (v === undefined) {
        v = forecastCustomerHub(c, h, d, overrideMap, events);
        cache.set(k, v);
      }
      return v;
    };
  }, [overrideMap, events]);
  const tFn = useMemo(() => makeTransitFn(hubs, branches, transitAware), [hubs, branches, transitAware]);

  const actualByHubDate = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of actuals) m.set(`${a.hubId}|${a.date}`, (m.get(`${a.hubId}|${a.date}`) ?? 0) + a.actualVolume);
    return m;
  }, [actuals]);
  const custWeekActual = useMemo(() => {
    const m = new Map<string, number[]>();
    const idxOf = new Map(pastWeeks.map((w, i) => [w, i]));
    for (const a of actuals) {
      if (!hubSet.has(a.hubId)) continue;
      const i = idxOf.get(startOfWeekSat(a.date));
      if (i === undefined) continue;
      let arr = m.get(a.customerId);
      if (!arr) ((arr = new Array(PAST_WEEKS).fill(0)), m.set(a.customerId, arr));
      arr[i] += a.actualVolume;
    }
    return m;
  }, [actuals, pastWeeks, hubSet]);

  // Sortation (own + received-in) per hub-day over the future window → capacity.
  const recvFuture = useMemo(
    () => receivedInByDay({ hubIds: hubs.map((h) => h.id), branches, dates: futureDates, own: ownDay, share: (o, b) => shareOf(shares, o, b), transit: (o, b) => tFn(o, b) }),
    [hubs, branches, futureDates, ownDay, shares, tFn]
  );
  const sortDay = (h: string, d: string) => ownDay(h, d) + (recvFuture.get(`${h}|${d}`) ?? 0);

  const hubCapacity = useMemo(
    () =>
      hubs
        .filter((h) => hubSet.has(h.id))
        .map((h) => {
          const total4 = futureDates.reduce((s, d) => s + ownDay(h.id, d), 0);
          let peak = 0;
          let peakDate = "";
          for (const d of futureDates) {
            const v = sortDay(h.id, d);
            if (v > peak) ((peak = v), (peakDate = d));
          }
          const util = h.capacity && h.capacity > 0 ? Math.round((peak / h.capacity) * 100) : null;
          return { h, total4, peak, peakDate, cap: h.capacity ?? null, util };
        })
        .filter((x) => x.total4 > 0)
        .sort((a, b) => (b.util ?? 0) - (a.util ?? 0)),
    [hubs, hubSet, futureDates, ownDay, recvFuture] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const overCap = useMemo(() => hubCapacity.filter((x) => x.util != null && x.util > 100), [hubCapacity]);

  // Accuracy per hub (filtered) over last complete weeks.
  const hubAccuracy = useMemo(
    () =>
      hubs
        .filter((h) => hubSet.has(h.id))
        .map((h) => {
          const rows = accWindow.map((d) => ({ forecast: ownDay(h.id, d), actual: actualByHubDate.get(`${h.id}|${d}`) ?? 0 })).filter((r) => r.actual > 0);
          const mape = meanAbsPctError(rows);
          return { h, acc: mape == null ? null : Math.max(0, 100 - mape), n: rows.length };
        })
        .filter((x) => x.n > 0),
    [hubs, hubSet, accWindow, ownDay, actualByHubDate]
  );
  const networkAccuracy = useMemo(() => {
    const rows = [...hubSet].flatMap((h) => accWindow.map((d) => ({ forecast: ownDay(h, d), actual: actualByHubDate.get(`${h}|${d}`) ?? 0 }))).filter((r) => r.actual > 0);
    const mape = meanAbsPctError(rows);
    return mape == null ? null : Math.max(0, 100 - mape);
  }, [hubSet, accWindow, ownDay, actualByHubDate]);

  // Customer volume anomalies (z-score on last complete week vs trailing).
  const custAnomalies = useMemo(() => {
    const out: Array<{ c: Customer; latest: number; mean: number; z: number; pct: number }> = [];
    for (const c of customers) {
      if (!c.active) continue;
      const wk = custWeekActual.get(c.id);
      if (!wk) continue;
      const base = wk.slice(0, PAST_WEEKS - 1).filter((v) => v > 0);
      const latest = wk[PAST_WEEKS - 1];
      if (base.length < 3 || latest <= 0) continue;
      const mean = base.reduce((s, v) => s + v, 0) / base.length;
      const sd = Math.sqrt(base.reduce((s, v) => s + (v - mean) ** 2, 0) / base.length);
      if (mean <= 0) continue;
      const z = sd > 0 ? (latest - mean) / sd : 0;
      const pct = ((latest - mean) / mean) * 100;
      if (Math.abs(z) >= 2 && Math.abs(pct) >= 25) out.push({ c, latest, mean, z, pct });
    }
    return out.sort((a, b) => Math.abs(b.z) - Math.abs(a.z));
  }, [customers, custWeekActual]);

  const liveEvents = useMemo(
    () => events.filter((e) => e.active !== false && e.endDate >= nowWeek && (isAll || hubSet.has(e.hubId) || (e.redistributeHubs ?? []).some((r) => hubSet.has(r.hubId)))),
    [events, nowWeek, isAll, hubSet]
  );

  const anomalies = useMemo(() => {
    const out: Anomaly[] = [];
    for (const x of overCap) out.push({ sev: (x.util ?? 0) > 115 ? "bad" : "warn", cat: "Capacity", title: `${hubName(x.h.id)} over capacity — ${x.util}%`, detail: `Peak sortation ${fmtN(x.peak)} vs cap ${fmtN(x.cap ?? 0)} on ${fmtDate(x.peakDate)}.` });
    for (const a of custAnomalies) out.push({ sev: Math.abs(a.z) >= 3 ? "bad" : "warn", cat: a.pct > 0 ? "Volume spike" : "Volume drop", title: `${a.c.name} ${a.pct > 0 ? "up" : "down"} ${Math.abs(a.pct).toFixed(0)}%`, detail: `Last full week ${fmtN(a.latest)} vs ${fmtN(Math.round(a.mean))} avg (z=${a.z.toFixed(1)}).` });
    for (const x of hubAccuracy) if (x.acc != null && x.acc < 80) out.push({ sev: x.acc < 70 ? "bad" : "warn", cat: "Accuracy", title: `${hubName(x.h.id)} accuracy ${x.acc.toFixed(0)}%`, detail: `vs actuals, last ${ACC_WEEKS} wks — consider recalibrating.` });
    for (const e of liveEvents) out.push({ sev: "info", cat: "Event", title: `${hubName(e.hubId)} — ${e.type}`, detail: `${fmtDate(e.startDate)} → ${fmtDate(e.endDate)}${e.redistributeVolume ? " · reroutes volume" : ""}.` });
    return out.sort((a, b) => SEV_RANK[a.sev] - SEV_RANK[b.sev]);
  }, [overCap, custAnomalies, hubAccuracy, liveEvents, hubName]);
  const sevCount = (s: Sev) => anomalies.filter((a) => a.sev === s).length;

  // Headline numbers
  const fourWk = useMemo(() => futureDates.reduce((s, d) => s + [...hubSet].reduce((a, h) => a + ownDay(h, d), 0), 0), [futureDates, hubSet, ownDay]);
  const wowPct = useMemo(() => {
    const w0 = curWeekDates.reduce((s, d) => s + [...hubSet].reduce((a, h) => a + ownDay(h, d), 0), 0);
    const prev = wkDates(addDays(nowWeek, -7)).reduce((s, d) => s + [...hubSet].reduce((a, h) => a + ownDay(h, d), 0), 0);
    return prev > 0 ? ((w0 - prev) / prev) * 100 : null;
  }, [curWeekDates, nowWeek, hubSet, ownDay]);

  // Package mix (4-week, filtered) for the donut.
  const pkgMix = useMemo(() => {
    let acc = emptyPackageSplit();
    for (const c of customers) for (const h of hubSet) for (const d of futureDates) {
      const v = cFcst(c, h, d);
      if (v > 0) acc = addPackageSplit(acc, splitPackageMix(v, laneMix.get(`${c.id}|${h}`)));
    }
    return acc;
  }, [customers, hubSet, futureDates, cFcst, laneMix]);

  // Volume by week: past actual (filtered) + future forecast (filtered).
  const weekly = useMemo(() => {
    const past = pastWeeks.map((w) => ({ label: fmtDate(addDays(w, 6)), value: Math.round(wkDates(w).reduce((s, d) => s + [...hubSet].reduce((a, h) => a + (actualByHubDate.get(`${h}|${d}`) ?? 0), 0), 0)), color: "#94a3b8" })).filter((p) => p.value > 0);
    const future = futureWeeks.map((w) => ({ label: fmtDate(addDays(w, 6)), value: Math.round(wkDates(w).reduce((s, d) => s + [...hubSet].reduce((a, h) => a + ownDay(h, d), 0), 0)), color: "#0f766e" }));
    return [...past, ...future];
  }, [pastWeeks, futureWeeks, hubSet, actualByHubDate, ownDay]);

  // Top customers (filtered, 4-week).
  const topCustomers = useMemo(
    () =>
      customers
        .map((c) => ({ c, value: futureDates.reduce((s, d) => s + [...hubSet].reduce((a, h) => a + cFcst(c, h, d), 0), 0) }))
        .filter((x) => x.value > 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, 10),
    [customers, futureDates, hubSet, cFcst]
  );

  // 4-week own per hub (all hubs) — drives the bypass volume math.
  const hubOwn4 = useMemo(() => {
    const m = new Map<string, number>();
    for (const h of hubs) m.set(h.id, futureDates.reduce((s, d) => s + ownDay(h.id, d), 0));
    return m;
  }, [hubs, futureDates, ownDay]);

  // Daily forecast (28d) vs same-weekday actual (last 4 complete weeks).
  const dailyCompare = useMemo(() => {
    const past0 = addDays(lastCompleteWeek, -21);
    return futureDates.map((d, i) => {
      const f = [...hubSet].reduce((s, h) => s + ownDay(h, d), 0);
      const ad = addDays(past0, i);
      const a = [...hubSet].reduce((s, h) => s + (actualByHubDate.get(`${h}|${ad}`) ?? 0), 0);
      const parts = fmtDow(d).split(" ");
      return { weekday: parts[0], date: parts[1] ?? d.slice(5), a: Math.round(f), b: a > 0 ? Math.round(a) : null };
    });
  }, [futureDates, lastCompleteWeek, hubSet, ownDay, actualByHubDate]);

  // Day-of-week shape (current week share of volume).
  const dow = useMemo(() => {
    const vals = curWeekDates.map((d) => [...hubSet].reduce((s, h) => s + ownDay(h, d), 0));
    const tot = vals.reduce((s, v) => s + v, 0) || 1;
    return curWeekDates.map((d, i) => ({ label: fmtDow(d).split(" ")[0], value: Math.round((vals[i] / tot) * 1000) / 10, vol: Math.round(vals[i]) }));
  }, [curWeekDates, hubSet, ownDay]);

  // LH loads inbound by hub (next 4 weeks).
  const lhByHub = useMemo(
    () =>
      hubs
        .filter((h) => hubSet.has(h.id))
        .map((h) => ({ h, loads: futureDates.reduce((s, d) => s + lhLoadsInOnDay(lhRows, new Set([h.id]), d), 0) }))
        .filter((x) => x.loads > 0)
        .sort((a, b) => b.loads - a.loads),
    [hubs, hubSet, futureDates, lhRows]
  );

  // Direct-lane (bypass) opportunities: branches getting H2H volume from an origin
  // where shipping DIRECT is at least as fast as routing via the home hub — so you
  // skip the re-sort at the destination.
  const bypassOps = useMemo(() => {
    const hubById = new Map(hubs.map((h) => [h.id, h]));
    const out: Array<{ b: Branch; o: Hub; vol: number; viaDays: number; directDays: number; save: number }> = [];
    for (const b of branches) {
      if (!isAll && !hubSet.has(b.hubId)) continue;
      const home = hubById.get(b.hubId);
      for (const o of hubs) {
        if (o.id === b.hubId) continue;
        const sh = shareOf(shares, o.id, b.id);
        if (sh <= 0) continue;
        const vol = Math.round((hubOwn4.get(o.id) ?? 0) * sh);
        if (vol < 2000) continue;
        const viaDays = transitDaysFor({ lat: o.lat, lng: o.lng }, { lat: home?.lat, lng: home?.lng });
        const directDays = transitDaysFor({ lat: o.lat, lng: o.lng }, { lat: b.lat, lng: b.lng });
        if (directDays <= viaDays) out.push({ b, o, vol, viaDays, directDays, save: viaDays - directDays });
      }
    }
    return out.sort((a, b) => b.save - a.save || b.vol - a.vol).slice(0, 12);
  }, [branches, hubs, isAll, hubSet, shares, hubOwn4]);

  // Hub summary table rows (own / sorted / util / accuracy / LH).
  const hubSummary = useMemo(() => {
    const accBy = new Map(hubAccuracy.map((x) => [x.h.id, x.acc]));
    const lhBy = new Map(lhByHub.map((x) => [x.h.id, x.loads]));
    return hubCapacity.map((x) => ({ ...x, acc: accBy.get(x.h.id) ?? null, lh: lhBy.get(x.h.id) ?? 0, sorted: x.peak }));
  }, [hubCapacity, hubAccuracy, lhByHub]);

  // Hub deep-dive (only when one hub is selected): sortation flow + MHE + hourly.
  const hubFocus = useMemo(() => {
    if (isAll) return null;
    const allHubIds = hubs.map((h) => h.id);
    let own = 0;
    let sentOut = 0;
    let receivedIn = 0;
    const recvCur = receivedInByDay({ hubIds: allHubIds, branches, dates: curWeekDates, own: ownDay, share: (o, b) => shareOf(shares, o, b), transit: (o, b) => tFn(o, b) });
    for (const d of curWeekDates) {
      own += ownDay(hubId, d);
      for (const b of branches) if (b.hubId !== hubId) sentOut += ownDay(hubId, d) * shareOf(shares, hubId, b.id);
      receivedIn += recvCur.get(`${hubId}|${d}`) ?? 0;
    }
    const sorted = own + receivedIn;
    const toBranches = own - sentOut + receivedIn;
    const flowRatio = own > 0 ? sorted / own : 1;
    // MHE: per-type peak day over the future window, scaled to sortation.
    const cap = { cartons: hubs.find((h) => h.id === hubId)?.mheCartons ?? null, polybags: hubs.find((h) => h.id === hubId)?.mhePolybag ?? null, irregular: hubs.find((h) => h.id === hubId)?.mheIrregular ?? null };
    let peak: PackageSplit = emptyPackageSplit();
    for (const d of futureDates) {
      let day = emptyPackageSplit();
      for (const c of customers) {
        const v = cFcst(c, hubId, d);
        if (v > 0) day = addPackageSplit(day, splitPackageMix(v, laneMix.get(`${c.id}|${hubId}`)));
      }
      if (day.cartons > peak.cartons) peak = { ...peak, cartons: day.cartons };
      if (day.polybags > peak.polybags) peak = { ...peak, polybags: day.polybags };
      if (day.irregular > peak.irregular) peak = { ...peak, irregular: day.irregular };
    }
    // Hourly availability (current week, summed).
    const byHour = new Array<number>(24).fill(0);
    for (const d of curWeekDates) {
      const a = hourlyAvailability(customers, lhRows, hubId, d, { overrides: overrideMap, events, loadCapacity: capByKey });
      for (let i = 0; i < 24; i++) byHour[i] += a.byHour[i].total;
    }
    return { own, sentOut, receivedIn, sorted, toBranches, flowRatio, cap, peak, byHour };
  }, [isAll, hubId, hubs, branches, customers, curWeekDates, futureDates, ownDay, cFcst, shares, tFn, laneMix, lhRows, overrideMap, events, capByKey]);

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }
  if (loading) return <Spinner />;

  const scopeLabel = isAll ? "All hubs · network" : hubs.find((h) => h.id === hubId)?.name ?? "—";
  const healthSegs = [
    { label: "Critical", value: sevCount("bad"), color: "#dc2626" },
    { label: "Warning", value: sevCount("warn"), color: "#d97706" },
    { label: "Info", value: sevCount("info"), color: "#0ea5e9" },
  ].filter((s) => s.value > 0);
  const pkgSegs = [
    { label: "Cartons", value: Math.round(pkgMix.cartons), color: TYPE_COLORS.cartons },
    { label: "Polybags", value: Math.round(pkgMix.polybags), color: TYPE_COLORS.polybags },
    { label: "Irregular", value: Math.round(pkgMix.irregular), color: TYPE_COLORS.irregular },
    { label: "Unspecified", value: Math.round(pkgMix.unspec), color: TYPE_COLORS.unspec },
  ].filter((s) => s.value > 0);
  const peakHour = hubFocus ? hubFocus.byHour.indexOf(Math.max(...hubFocus.byHour)) : 0;

  return (
    <div className="space-y-4">
      <PageHeader title="Command Center" subtitle="Network health, anomalies, forecast, sortation capacity and flow — one view. Pick a hub to drill in.">
        <span className="text-xs text-slate-400">{scopeLabel}</span>
      </PageHeader>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>}

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-56">
            <Field label="Hub">
              <Select value={hubId} onChange={(e) => setHubId(e.target.value)}>
                <option value={ALL}>All hubs (network)</option>
                {hubs.map((h) => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </Select>
            </Field>
          </div>
          <label className="flex cursor-pointer items-center gap-2 pb-2 text-xs text-slate-600" title="Land H2H/Direct inbound on ship date + transit days">
            <input type="checkbox" checked={transitAware} onChange={(e) => setTransitAware(e.target.checked)} />
            Transit-aware arrivals
          </label>
          <p className="text-xs text-slate-400">Forecast: next {FUTURE_WEEKS} wks · accuracy & anomalies: last complete weeks.</p>
        </div>
      </Card>

      {/* KPI strip */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
        <Kpi label="4-week forecast" value={fmtN(fourWk)} sub={scopeLabel} />
        <Kpi label="WoW change" value={wowPct == null ? "—" : `${wowPct > 0 ? "+" : ""}${wowPct.toFixed(1)}%`} sub="current vs last week" tone={wowPct == null ? undefined : Math.abs(wowPct) > 15 ? "bad" : Math.abs(wowPct) > 7 ? "warn" : "good"} />
        <Kpi label="Forecast accuracy" value={networkAccuracy == null ? "—" : `${networkAccuracy.toFixed(0)}%`} sub={`last ${ACC_WEEKS} wks`} tone={networkAccuracy == null ? undefined : networkAccuracy >= 90 ? "good" : networkAccuracy >= 80 ? "warn" : "bad"} />
        <Kpi label="Hubs over capacity" value={fmtN(overCap.length)} sub="next 4 wks · sortation" tone={overCap.length ? "bad" : "good"} />
        <Kpi label="Active customers" value={fmtN(customers.filter((c) => c.active).length)} sub={`${hubs.length} hubs`} />
        <Kpi label="Anomalies" value={fmtN(anomalies.length)} sub={`${sevCount("bad")} critical · ${sevCount("warn")} warn`} tone={sevCount("bad") ? "bad" : sevCount("warn") ? "warn" : "good"} />
      </div>

      {/* Anomaly feed + health donut */}
      <div className="grid items-start gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <div className="mb-1 text-sm font-medium text-slate-700">Anomaly feed</div>
          <p className="mb-3 text-[11px] text-slate-400">Ranked by severity: capacity, volume spikes/drops (z-score), weak accuracy, active events.</p>
          {anomalies.length === 0 ? (
            <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">✓ All clear — nothing flagged across capacity, volume, accuracy or events.</p>
          ) : (
            <div className="max-h-[420px] space-y-1.5 overflow-auto pr-1">
              {anomalies.map((a, i) => (
                <div key={i} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${SEV_STYLE[a.sev]}`}>
                  <span>{SEV_DOT[a.sev]}</span>
                  <div className="min-w-0">
                    <div className="font-medium">
                      <span className="mr-2 rounded bg-white/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">{a.cat}</span>
                      {a.title}
                    </div>
                    <div className="text-[11px] opacity-80">{a.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card>
          <div className="mb-1 text-sm font-medium text-slate-700">Network health</div>
          <p className="mb-2 text-[11px] text-slate-400">Anomalies by severity.</p>
          {healthSegs.length === 0 ? (
            <p className="py-8 text-center text-sm text-emerald-600">✓ All clear</p>
          ) : (
            <div className="flex items-center gap-4">
              <Donut segments={healthSegs} />
              <div className="space-y-1 text-xs">
                {healthSegs.map((s) => (
                  <div key={s.label} className="flex items-center gap-2">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
                    <span className="text-slate-600">{s.label}</span>
                    <span className="ml-auto tabular-nums font-medium text-slate-700">{s.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Volume by week */}
      <Card>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-medium text-slate-700">Volume by week · {scopeLabel}</div>
          <div className="flex items-center gap-3 text-[11px] text-slate-500">
            <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded-sm bg-slate-400" /> actual</span>
            <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded-sm bg-teal-700" /> forecast</span>
          </div>
        </div>
        <BarsChart data={weekly} showLine lineLabel="trend" />
      </Card>

      {/* Daily forecast vs same-weekday actuals */}
      <Card>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-medium text-slate-700">Daily forecast vs recent actuals · 28 days</div>
          <div className="flex items-center gap-3 text-[11px] text-slate-500">
            <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-3 rounded-sm bg-teal-700" /> forecast</span>
            <span className="flex items-center gap-1"><span className="inline-block h-0.5 w-4 border-t border-dashed border-slate-500" /> actual (same weekday, last 4 wks)</span>
          </div>
        </div>
        <p className="mb-2 text-[11px] text-slate-400">Each forecast day (Sat→Fri) vs the actual from the same weekday in the trailing 4 complete weeks. {scopeLabel}.</p>
        <DailyBarsLine points={dailyCompare} />
      </Card>

      {/* Day-of-week shape */}
      <Card>
        <div className="mb-1 text-sm font-medium text-slate-700">Day-of-week shape · current week</div>
        <p className="mb-2 text-[11px] text-slate-400">Each weekday's share of the week's volume ({scopeLabel}). Drives how daily volume (and staffing) peaks.</p>
        <BarsChart data={dow.map((d) => ({ label: d.label, value: d.value, color: "#6366f1", sub: `${fmtN(d.vol)}` }))} />
      </Card>

      {/* Capacity util + package mix */}
      <div className="grid items-start gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <div className="mb-1 text-sm font-medium text-slate-700">Capacity utilization by hub · peak day (next 4 wks)</div>
          <p className="mb-3 text-[11px] text-slate-400">Peak sortation (own + received-in) ÷ daily capacity. Red &gt; 100%, amber &gt; 85%.</p>
          {hubCapacity.filter((x) => x.util != null).length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">No hub capacities set.</p>
          ) : (
            <HBarList
              max={Math.max(100, ...hubCapacity.map((x) => x.util ?? 0))}
              unit="%"
              items={hubCapacity.filter((x) => x.util != null).map((x) => ({ label: hubName(x.h.id), value: x.util as number, tone: (x.util as number) > 100 ? "bad" : (x.util as number) > 85 ? "warn" : "ok" }))}
            />
          )}
        </Card>
        <Card>
          <div className="mb-1 text-sm font-medium text-slate-700">Package-type mix · 4 wks</div>
          {pkgSegs.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">No package mix on lanes.</p>
          ) : (
            <div className="flex items-center gap-4">
              <Donut segments={pkgSegs} />
              <div className="space-y-1 text-xs">
                {pkgSegs.map((s) => (
                  <div key={s.label} className="flex items-center gap-2">
                    <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
                    <span className="text-slate-600">{s.label}</span>
                    <span className="ml-auto tabular-nums font-medium text-slate-700">{fmtN(s.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Accuracy + top customers */}
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Card>
          <div className="mb-1 text-sm font-medium text-slate-700">Forecast accuracy by hub · last {ACC_WEEKS} wks</div>
          <p className="mb-3 text-[11px] text-slate-400">100% − MAPE. Lower = recalibrate.</p>
          {hubAccuracy.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">No actuals yet to score.</p>
          ) : (
            <HBarList max={100} unit="%" items={[...hubAccuracy].sort((a, b) => (a.acc ?? 0) - (b.acc ?? 0)).map((x) => ({ label: hubName(x.h.id), value: Math.round(x.acc ?? 0), tone: (x.acc ?? 0) >= 90 ? "ok" : (x.acc ?? 0) >= 80 ? "warn" : "bad" }))} />
          )}
        </Card>
        <Card>
          <div className="mb-1 text-sm font-medium text-slate-700">Top customers · next 4 wks</div>
          <p className="mb-3 text-[11px] text-slate-400">Largest shippers by forecast volume.</p>
          <HBarList items={topCustomers.map((x, i) => ({ label: x.c.name, value: Math.round(x.value), color: HUB_PALETTE[i % HUB_PALETTE.length] }))} />
        </Card>
      </div>

      {/* Hub summary table */}
      <Card className="p-0">
        <div className="border-b border-slate-200 px-5 py-2.5">
          <div className="text-sm font-medium text-slate-700">Hub summary · next 4 weeks</div>
          <p className="mt-0.5 text-[11px] text-slate-400">Own volume, peak-day sortation (own + received), capacity utilization, recent accuracy, and inbound LH.</p>
        </div>
        <div className="max-h-[50vh] overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 [&>th]:sticky [&>th]:top-0 [&>th]:z-10 [&>th]:bg-white">
                <th className="px-5 py-2 font-medium">Hub</th>
                <th className="px-3 py-2 text-right font-medium">Own (4wk)</th>
                <th className="px-3 py-2 text-right font-medium">Peak sortation/day</th>
                <th className="px-3 py-2 text-right font-medium">Cap/day</th>
                <th className="px-3 py-2 text-right font-medium">Util</th>
                <th className="px-3 py-2 text-right font-medium">Accuracy</th>
                <th className="px-5 py-2 text-right font-medium">LH loads</th>
              </tr>
            </thead>
            <tbody>
              {hubSummary.map((x) => {
                const utilCls = x.util == null ? "text-slate-400" : x.util > 100 ? "text-red-600 font-semibold" : x.util > 85 ? "text-amber-600" : "text-emerald-600";
                const accCls = x.acc == null ? "text-slate-400" : x.acc >= 90 ? "text-emerald-600" : x.acc >= 80 ? "text-amber-600" : "text-red-600";
                return (
                  <tr key={x.h.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-5 py-1.5 font-medium text-slate-800">{x.h.code || x.h.name}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{fmtN(x.total4)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{fmtN(x.peak)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{x.cap == null ? "—" : fmtN(x.cap)}</td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${utilCls}`}>{x.util == null ? "—" : `${x.util}%`}</td>
                    <td className={`px-3 py-1.5 text-right tabular-nums ${accCls}`}>{x.acc == null ? "—" : `${x.acc.toFixed(0)}%`}</td>
                    <td className="px-5 py-1.5 text-right tabular-nums text-slate-700">{fmtN(x.lh)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Customer allocation + LH inbound */}
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Card className="p-0">
          <div className="border-b border-slate-200 px-5 py-2.5">
            <div className="text-sm font-medium text-slate-700">Customer → hub allocation · top shippers</div>
            <p className="mt-0.5 text-[11px] text-slate-400">Where each customer's volume is assigned, with daily base, growth and 4-week forecast.</p>
          </div>
          <div className="max-h-[50vh] overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 [&>th]:sticky [&>th]:top-0 [&>th]:z-10 [&>th]:bg-white">
                  <th className="px-5 py-2 font-medium">Customer</th>
                  <th className="px-3 py-2 text-right font-medium">Daily base</th>
                  <th className="px-3 py-2 text-right font-medium">Growth</th>
                  <th className="px-3 py-2 font-medium">Allocation</th>
                  <th className="px-5 py-2 text-right font-medium">4wk fcst</th>
                </tr>
              </thead>
              <tbody>
                {topCustomers.map((x) => (
                  <tr key={x.c.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-5 py-1.5 font-medium text-slate-800">{x.c.name}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmtN(x.c.dailyBase)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{x.c.growth}%</td>
                    <td className="px-3 py-1.5 text-slate-600">{x.c.allocations.length ? x.c.allocations.map((a) => `${hubName(a.hubId)} ${a.percentage}%`).join(" · ") : "—"}</td>
                    <td className="px-5 py-1.5 text-right tabular-nums font-medium text-slate-700">{fmtN(Math.round(x.value))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <Card className="p-0">
          <div className="border-b border-slate-200 px-5 py-2.5">
            <div className="text-sm font-medium text-slate-700">LH inbound by hub · next 4 weeks</div>
            <p className="mt-0.5 text-[11px] text-slate-400">Scheduled linehaul loads arriving at each hub.</p>
          </div>
          {lhByHub.length === 0 ? (
            <p className="px-5 py-6 text-sm text-slate-400">No LH schedule for these hubs.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-5 py-2 font-medium">Hub</th>
                  <th className="px-5 py-2 text-right font-medium">Loads (4wk)</th>
                </tr>
              </thead>
              <tbody>
                {lhByHub.map((x) => (
                  <tr key={x.h.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-5 py-1.5 font-medium text-slate-800">{x.h.code || x.h.name}</td>
                    <td className="px-5 py-1.5 text-right tabular-nums text-slate-700">{fmtN(x.loads)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      {/* Direct-lane (bypass) opportunities */}
      <Card className="p-0">
        <div className="border-b border-slate-200 px-5 py-2.5">
          <div className="text-sm font-medium text-slate-700">Direct-lane (bypass) opportunities</div>
          <p className="mt-0.5 text-[11px] text-slate-400">Branches getting H2H volume where shipping <b>Direct</b> from the origin is as fast or faster than routing via the home hub — set a Direct lane to skip the re-sort.</p>
        </div>
        {bypassOps.length === 0 ? (
          <p className="px-5 py-6 text-sm text-slate-400">No bypass opportunities — H2H flows already route through the fastest path.</p>
        ) : (
          <div className="max-h-[50vh] overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 [&>th]:sticky [&>th]:top-0 [&>th]:z-10 [&>th]:bg-white">
                  <th className="px-5 py-2 font-medium">Branch</th>
                  <th className="px-3 py-2 font-medium">From hub</th>
                  <th className="px-3 py-2 text-right font-medium">H2H vol (4wk)</th>
                  <th className="px-3 py-2 text-right font-medium">Via hub</th>
                  <th className="px-3 py-2 text-right font-medium">Direct</th>
                  <th className="px-5 py-2 text-right font-medium">Days saved</th>
                </tr>
              </thead>
              <tbody>
                {bypassOps.map((x, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className="px-5 py-1.5 font-medium text-slate-800">{x.b.name}</td>
                    <td className="px-3 py-1.5 text-slate-600">{hubName(x.o.id)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{fmtN(x.vol)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{x.viaDays}d</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{x.directDays}d</td>
                    <td className={`px-5 py-1.5 text-right tabular-nums font-medium ${x.save > 0 ? "text-emerald-600" : "text-slate-500"}`}>{x.save > 0 ? `−${x.save}d` : "same"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Hub deep-dive (single hub) */}
      {hubFocus && (
        <>
          <Card>
            <div className="mb-1 text-sm font-medium text-slate-700">Sortation flow · current week · {scopeLabel}</div>
            <p className="mb-3 text-[11px] text-slate-400">Sorted = own + received-in; routes out to local branches + H2H out. In = Out.</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              <FlowStat label="Originates here" value={Math.round(hubFocus.own)} tone="slate" />
              <FlowStat label="Received in (H2H)" value={Math.round(hubFocus.receivedIn)} tone="sky" />
              <FlowStat label="Sorted (throughput)" value={Math.round(hubFocus.sorted)} tone="teal" />
              <FlowStat label="→ To local branches" value={Math.round(hubFocus.toBranches)} tone="slate" />
              <FlowStat label="→ H2H out" value={Math.round(hubFocus.sentOut)} tone="amber" />
            </div>
          </Card>

          <div className="grid items-start gap-4 lg:grid-cols-2">
            <Card className="p-0">
              <div className="border-b border-slate-200 px-5 py-2.5">
                <div className="text-sm font-medium text-slate-700">MHE capacity by type · peak day</div>
                <p className="mt-0.5 text-[11px] text-slate-400">Peak-day sortation (own mix ×{hubFocus.flowRatio.toFixed(2)} to total handled) vs daily MHE cap.</p>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-5 py-2 font-medium">Type</th>
                    <th className="px-3 py-2 text-right font-medium">Cap/day</th>
                    <th className="px-3 py-2 text-right font-medium">Peak</th>
                    <th className="px-5 py-2 text-right font-medium">Util</th>
                  </tr>
                </thead>
                <tbody>
                  {([
                    { label: "Cartons", color: TYPE_COLORS.cartons, cap: hubFocus.cap.cartons, peak: hubFocus.peak.cartons * hubFocus.flowRatio },
                    { label: "Polybags", color: TYPE_COLORS.polybags, cap: hubFocus.cap.polybags, peak: hubFocus.peak.polybags * hubFocus.flowRatio },
                    { label: "Irregular", color: TYPE_COLORS.irregular, cap: hubFocus.cap.irregular, peak: hubFocus.peak.irregular * hubFocus.flowRatio },
                  ] as const).map((r) => {
                    const util = r.cap && r.cap > 0 ? (r.peak / r.cap) * 100 : null;
                    const cls = r.cap == null ? "text-slate-400" : util != null && util > 100 ? "text-red-600" : util != null && util > 85 ? "text-amber-600" : "text-emerald-600";
                    return (
                      <tr key={r.label} className="border-b border-slate-100 last:border-0">
                        <td className="px-5 py-2 font-medium text-slate-800">
                          <span className="mr-2 inline-block h-2.5 w-2.5 rounded-sm align-middle" style={{ backgroundColor: r.color }} />
                          {r.label}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700">{r.cap == null ? "—" : fmtN(r.cap)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700">{fmtN(Math.round(r.peak))}</td>
                        <td className={`px-5 py-2 text-right tabular-nums font-medium ${cls}`}>{util == null ? "—" : `${Math.round(util)}%`}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
            <Card>
              <div className="mb-1 text-sm font-medium text-slate-700">Volume availability · current week hourly</div>
              <p className="mb-2 text-[11px] text-slate-400">When volume lands across the day. Peak hour <b className="text-slate-600">{String(peakHour).padStart(2, "0")}:00</b>.</p>
              <HourBars byHour={hubFocus.byHour} />
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: "good" | "warn" | "bad" }) {
  const c = tone === "bad" ? "text-red-600" : tone === "warn" ? "text-amber-600" : tone === "good" ? "text-emerald-600" : "text-slate-900";
  const dot = tone === "bad" ? "bg-red-500" : tone === "warn" ? "bg-amber-500" : tone === "good" ? "bg-emerald-500" : "";
  return (
    <Card>
      <p className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">
        {dot && <span className={`inline-block h-1.5 w-1.5 rounded-full ${dot}`} />}
        {label}
      </p>
      <p className={`mt-1 text-xl font-semibold tabular-nums ${c}`}>{value}</p>
      {sub && <p className="mt-0.5 text-[10px] text-slate-400">{sub}</p>}
    </Card>
  );
}

function FlowStat({ label, value, tone }: { label: string; value: number; tone: "slate" | "amber" | "sky" | "teal" }) {
  const c = tone === "teal" ? "text-brand-700" : tone === "amber" ? "text-amber-600" : tone === "sky" ? "text-sky-600" : "text-slate-700";
  return (
    <div className="rounded-lg border border-slate-200 px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-0.5 text-base font-semibold tabular-nums ${c}`}>{fmtN(value)}</p>
    </div>
  );
}
